import CardCommon from '@op/components/CardCommon';
import CommonTitle from '../CommonTitle';
import React, { useMemo, useState } from 'react';
import { Col, Row } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import {
  StreamingCardInfo,
  VideoCardInfo,
} from '../../../component/FeedsInfoCard';
import { SORT_STREAMING_OPTIONS, SORT_VIDEO_OPTIONS } from '../../../constant';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { useSteamingData } from '../../../hook';
import { LanguageType } from '@op/types/common';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import { useTrendingRequest } from '@op/pages/Overview/TrendingPublications/hooks/useTrendingRequest';
import { DBO_TM } from '@op/utils/format';
import { IRealtimeOpinionsTabType } from '../../../type';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { pickSupportedFilters } from '@op/utils/value';

export default function Feeds({
  filter,
  gameInfo,
  endTime,
  deadline
}: {
  filter: any;
  gameInfo: any;
  endTime: string;
  deadline: IRealtimeOpinionsTabType;
}) {
  const { formatOptionsMessage } = useLanguage();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const isToday = useMemo(() => DBO_TM.isToday(filter), [filter]);
  return (
    <CardCommon
      className="dbo-trending-content-cardCommon"
      contentCard
      title={
        <CommonTitle
          title={"Trending Content"}
          endTime={endTime}
        />
      }
      operations={[
        {
          element: (
            <TranslateSelect
              langOptions={formatOptionsMessage(languageSelectOptions)}
              lang={language}
              isSingle={true}
              getChange={(e: any) => {
                setLanguage(e);
                buriedBtnPoint('RO_Feeds_Language', e);
              }}
            />
          ),
        },
      ]}
      alwaysShowOperations
    >
      <Row gap={10}>
        {[
          {
            title: 'Top10 Videos',
            options: SORT_VIDEO_OPTIONS,
            isVideo: true,
            Component: VideoContent,
          },
          {
            title: 'Twitch Streaming（Live Today）',
            options: SORT_STREAMING_OPTIONS,
            isVideo: false,
            Component: SteamContent,
          },
        ].map(({ title, options, Component, ...props }, index) => {
          return (
            <Col span={12} key={title + index}>
              <Component
                {...props}
                title={title}
                options={options}
                language={language}
                defaultValue={options[0].value}
                filter={filter}
                key={index}
                gameInfo={gameInfo}
                deadline={deadline}
                isToday={isToday}
              ></Component>
            </Col>
          );
        })}
      </Row>
    </CardCommon>
  );
}

function VideoContent({
  title,
  options,
  defaultValue,
  filter,
  language,
  gameInfo,
  deadline,
  isToday
}: {
  title: string;
  isVideo: boolean;
  options: { text: string; value: string }[];
  defaultValue?: string;
  filter?: any;
  language: LanguageType;
  gameInfo: any;
  deadline: IRealtimeOpinionsTabType;
  isToday: boolean;
}) {
  const [sortItem, setSortItem] = useState(defaultValue);
  const { formatMessage } = useLanguage();

  const params = useMemo(() => {
    if (isToday) {
      return {
        ...filter,
        ...gameInfo,
        review_types: ['posts_video', 'stream_titles'],
        media_types: ['video', 'live'],
        need_many_hour_ago: isToday,
        many_hour_ago: deadline === 'last_hour' ? 1 : 0
      };
    }
    return {
      ...filter,
      ...gameInfo,
      review_types: ['posts_video', 'stream_titles'],
      media_types: ['video', 'live'],
    };
  }, [sortItem, filter]);

  const { data, loading } = useTrendingRequest({
    filter: params,
    type: 'video',
    requestMax: false,
    extraFilter: {
      page_size: 10,
      sort_item: sortItem,
      sort_model: 'desc',
    }
  });

  return (
    <CardCommon
      hasTitleArrow
      title={formatMessage({ id: title })}
      dropdownsFilterBeforeElement={
        <SortBySelect
          defaultValue={sortItem}
          onChange={(key: string, value: any) => {
            setSortItem(value);
            buriedBtnPoint('RO_VideoContent_Dropdown', value);
          }}
          defaultParams={{
            options,
            needFormatMessage: true,
            btnTextPrefix: formatMessage({ id: 'Sort By' }),
            matchButtonWidth: true,
            // dealwithButton: (selectValue: string, { textMap }: any) => {
            //   return textMap[selectValue];
            // },
          }}
        />
      }
      handleTitleOnClick={() => {
        const gameid = gameInfo?.edition_unified_id;
        goStatsDetailAction({
          path: `/opinion/Community/Video?${gameid ? `gameid=${gameid}&` : ''}by=${sortItem}&order=desc`,
          params: filter,
          history,
        });
        buriedBtnPoint('RO_VideoContent_Title');
      }}
    >
      <CardCommon
        className="dbo-trending-video-cardCommon"
        contentCard
        loading={loading}
        empty={data.total === 0}
        emptyHeight={728}
        loadingHeight={728}
      >
        {data.list.map((item: any, index: number) => {
          return (
            <VideoCardInfo
              lang={language}
              data={item}
              key={item.comment_uin + index}
              gameInfo={gameInfo}
            ></VideoCardInfo>
          );
        })}
      </CardCommon>
    </CardCommon>
  );
}

function SteamContent({
  title,
  options,
  defaultValue,
  filter,
  language,
  gameInfo,
  deadline,
  isToday
}: {
  title: string;
  isVideo: boolean;
  options: { text: string; value: string }[];
  defaultValue?: string;
  filter?: any;
  language: LanguageType;
  gameInfo: any;
  deadline: IRealtimeOpinionsTabType;
  isToday: boolean;
}) {
  const [sortItem, setSortItem] = useState(defaultValue);
  const { formatMessage } = useLanguage();
  const params = useMemo(() => {
    const supportFilterKey = [
      'start_time',
      'end_time',
      'search_text',
      'edition_unified_id',
      "entity_type",
      "id_type",
      "should_include_timeline",
      "only_live_streaming",
      "kol_type",
      "date_type",
      "sort_item",
      "sort_model",
      "page",
      "page_size",
    ];
    const cleanFilter = pickSupportedFilters(filter, supportFilterKey)
    if (isToday) {
      // 今天 need_many_hour_ago:true
      return {
        ...cleanFilter,
        ...gameInfo,
        // only_live_streaming: true,
        sort_item: sortItem,
        sort_model: 'desc',
        page: 1,
        page_size: 10,
        need_many_hour_ago: isToday,
        many_hour_ago: deadline === 'last_hour' ? 1 : 0
      };
    }
    return {
      ...cleanFilter,
      // only_live_streaming: true,
      sort_item: sortItem,
      sort_model: 'desc',
      page: 1,
      page_size: 10
    };
  }, [sortItem, filter]);

  const { data, loading } = useSteamingData({
    filter: params,
  });

  return (
    <CardCommon
      title={formatMessage({ id: title })}
      dropdownsFilterBeforeElement={
        <SortBySelect
          defaultValue={sortItem}
          onChange={(key: string, value: any) => {
            setSortItem(value);
            buriedBtnPoint('RO_StreamContent_Dropdown', value);
          }}
          defaultParams={{
            options,
            needFormatMessage: true,
            btnTextPrefix: formatMessage({ id: 'Sort By' }),
            matchButtonWidth: true,
            // dealwithButton: (selectValue: string, { textMap }: any) => {
            //   return textMap[selectValue];
            // },
          }}
        />
      }
      hasTitleArrow
      handleTitleOnClick={() => {
        const gameid = gameInfo?.edition_unified_id;
        goStatsDetailAction({
          path: `/opinion/Realtime/RealtimeStreaming?${gameid ? `gameid=${gameid}&` : ''}&status=day`,
          params: filter,
          history,
        });
        buriedBtnPoint('RO_StreamContent_Title');
      }}
    >
      <CardCommon
        className="dbo-trending-stream-cardCommon"
        contentCard
        loading={loading}
        empty={data.length === 0}
        emptyHeight={728}
        loadingHeight={728}
      >
        {data.map((item: any, index: number) => {
          return (
            <StreamingCardInfo
              data={item}
              lang={language}
              key={item.live_id + index}
              gameInfo={gameInfo}
              filter={filter}
            ></StreamingCardInfo>
          );
        })}
      </CardCommon>
    </CardCommon>
  );
}
